/**
 * Batch Request Hook
 *
 * Provides React hooks for making batched API requests
 * to overcome HTTP/1.1 connection pooling limitations.
 */

import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseMutationOptions,
} from 'react-query';
import { batchRequestManager, BatchRequest } from './batchRequestManager';
import { RouterAPIData } from 'features/common/types';
import { checkSessionTimeout } from './proxy';

// Define the expected result structure from batch requests
interface BatchResult<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
  title?: string;
}

// Define options for batched mutation
interface BatchedMutationOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void
> extends Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'> {
  checkSession?: boolean;
}

/**
 * Hook for making a batched query request
 * @param key Query key for react-query caching
 * @param request The API request to batch
 * @param options React Query options
 */
export function useBatchedQuery<TData = unknown>(
  key: unknown[],
  request: BatchRequest,
  options?: Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TData>(
    key,
    async (): Promise<TData> => {
      const result = await batchRequestManager.queueRequest<BatchResult<TData>>(
        request
      );
      // Handle both single response and batch responses
      return (result?.data || result) as TData;
    },
    options
  );
}

/**
 * Hook for making a batched mutation request
 * @param request The API request to batch
 * @param options Mutation options
 */
export function useBatchedMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void
>(
  request: (variables: TVariables) => BatchRequest,
  options?: BatchedMutationOptions<TData, TError, TVariables>
) {
  return useMutation<TData, TError, TVariables>(
    async (variables: TVariables) => {
      const batchRequest = request(variables);
      const result = await batchRequestManager.queueRequest<BatchResult<TData>>(
        batchRequest
      );
      // Apply session timeout check if configured
      if (options?.checkSession) {
        return checkSessionTimeout({ data: { result } } as unknown);
      }
      return result?.data || result;
    },
    options
  );
}

/**
 * Utility to batch multiple requests together
 *
 * Usage:
 * const results = await batchRequests([
 *   { action: 'ConnectDB', method: 'Locale', data: [{}] },
 *   { action: 'FrmLayout', method: 'get', data: [{}] },
 *   { action: 'FrmLocalization', method: 'getLocalizations', data: [{}] },
 * ]);
 */
export async function batchRequests(
  requests: BatchRequest[]
): Promise<unknown[]> {
  // Queue all requests at once
  const promises = requests.map((req) => batchRequestManager.queueRequest(req));
  return Promise.all(promises);
}

export default {
  useBatchedQuery,
  useBatchedMutation,
  batchRequests,
};
